import Link from "@/components/link";
import { ArrowLeft, Download } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { useDocumentTitle } from "@/lib/use-document-title";

export default function Flow() {
  useDocumentTitle("A Gentle Intro to Flow Matching");

  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [showPreview, setShowPreview] = useState(true);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    // Set canvas dimensions to match window size
    const resizeCanvas = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };

    resizeCanvas();
    window.addEventListener("resize", resizeCanvas);

    // Flow parameters
    const flowLines = 3;
    const curves: {
      x: number;
      y: number;
      amplitude: number;
      frequency: number;
      speed: number;
      width: number;
      phase: number;
      color: string;
    }[] = [];

    // Initialize curves with different parameters
    for (let i = 0; i < flowLines; i++) {
      curves.push({
        x: 0,
        y: canvas.height * (0.3 + i * 0.2),
        amplitude: 100 + Math.random() * 50,
        frequency: 0.001 + Math.random() * 0.002,
        speed: 0.0005 + Math.random() * 0.001,
        width: 15 + Math.random() * 20,
        phase: Math.random() * Math.PI * 2,
        color: `rgba(91, 206, 230, ${0.2 + i * 0.1})`,
      });
    }

    // Animation loop
    let animationId: number;
    let time = 0;

    const animate = () => {
      time += 1;
      ctx.clearRect(0, 0, canvas.width, canvas.height);

      // Draw each curve
      curves.forEach((curve) => {
        ctx.beginPath();

        // Update phase
        curve.phase += curve.speed;

        // Draw the curve as a path
        for (let x = 0; x < canvas.width; x += 5) {
          const y =
            curve.y +
            Math.sin(x * curve.frequency + curve.phase) * curve.amplitude;

          if (x === 0) {
            ctx.moveTo(x, y);
          } else {
            ctx.lineTo(x, y);
          }
        }

        // Style and stroke the path
        ctx.strokeStyle = curve.color;
        ctx.lineWidth = curve.width;
        ctx.lineCap = "round";
        ctx.lineJoin = "round";
        ctx.stroke();
      });

      animationId = requestAnimationFrame(animate);
    };

    animate();

    return () => {
      window.removeEventListener("resize", resizeCanvas);
      cancelAnimationFrame(animationId);
    };
  }, []);

  return (
    <main className="min-h-screen bg-white flex flex-col relative overflow-hidden">
      <canvas
        ref={canvasRef}
        className="absolute top-0 left-0 w-full h-full pointer-events-none"
        style={{ zIndex: 0 }}
      />

      <div className="container mx-auto py-8 px-4 md:px-6 flex-grow relative z-10">
        <Link
          href="/"
          className="inline-flex items-center text-gray-700 hover:text-gray-900 mb-8"
        >
          <ArrowLeft size={16} className="mr-2" />
          Back to home
        </Link>

        <div className="max-w-4xl mx-auto text-center mt-10">
          <h1 className="text-5xl font-bold mb-6">
            A Gentle Intro to Flow Matching
          </h1>
          {/* <p className="text-xl italic mb-12"> */}
          {/* A gentle introduction to  */}
          {/* the wonderful MIT lecture on flow-based */}
          {/* generative models */}
          {/* </p> */}

          <p className="text-lg text-gray-600 my-20">Video coming soon...</p>

          <div className="w-full max-w-3xl mx-auto px-4 md:px-12 mb-8">
            <p className="text-lg text-gray-900 mb-4">
              The following PDF includes the introductory material for flow
              matching.
            </p>
            <div className="mx-auto mb-6 max-w-md rounded-xl border border-gray-200/70 bg-white/60 px-5 py-4 text-left shadow-sm backdrop-blur-sm">
              <span className="text-xs uppercase tracking-[0.3em] text-gray-500">
                Disclaimer
              </span>
              <p className="mt-2 text-sm leading-relaxed text-gray-600">
                This is a draft, and I'm still writing it. It still has a few
                fake graphs and may contain errors. It's also missing one or two
                proofs.
              </p>
            </div>

            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <a
                href="/docs/flow.pdf"
                download
                className="inline-flex items-center gap-2 px-6 py-3 bg-white border-2 border-black rounded hover:bg-gray-50 transition-colors"
              >
                <Download size={20} />
                <span>Download</span>
              </a>
              <button
                onClick={() => setShowPreview(!showPreview)}
                className="text-gray-700 hover:text-gray-900 underline"
              >
                {showPreview ? "Hide preview" : "Preview"}
              </button>
            </div>

            {showPreview && (
              <div className="mt-6 w-full h-[600px] border-2 border-gray-300 rounded">
                <iframe
                  src="/docs/flow.pdf"
                  className="w-full h-full"
                  title="Flow Matching PDF Preview"
                />
              </div>
            )}
          </div>
        </div>
      </div>
    </main>
  );
}
